import React, { useState } from 'react';
import '../../App.css';
import { AppHeader , HeaderIcons } from '../shared';
import Footer from '../shared/Footer';
import { formatRoomCode } from '../../utils/roomCode';
import { useTranslation } from '../../i18n';

interface PremiumHostSetupProps {
  onBack: () => void;
  onCreateHost: (roomCode: string) => void;
}

const PremiumHostSetup: React.FC<PremiumHostSetupProps> = ({ onBack, onCreateHost }) => {
  const [roomCode, setRoomCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useTranslation();

  const premiumWord = t('lobby.premiumHostSetup.premiumWord');
  const subtitle = t('lobby.premiumHostSetup.subtitle', { premiumWord });
  const [subtitleBefore, subtitleAfter] = subtitle.split(premiumWord);

  const handleCreateHost = () => {
    if (!roomCode.trim()) {
      return;
    }

    const roomCodeUpper = roomCode.trim().toUpperCase().slice(0, 4);
    if (roomCodeUpper.length !== 4) {
      return;
    }

    setIsCreating(true);
    onCreateHost(roomCodeUpper);
  };

  const handleRoomCodeChange = (value: string) => {
    setRoomCode(formatRoomCode(value));
  };

  const handleRoomCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleRoomCodeChange(e.clipboardData.getData('text'));
  };

  return (
    <div className="app" style={{ position: 'relative', paddingBottom: '100px' }}>
      <AppHeader
        logo={null}
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBack,
          tooltip: t('common.backToHome')
        }}
      />
      <div className="container player-content-wrapper" style={{ marginTop: '5px', marginBottom: '100px' }}>
        <p className="subtitle">
          {subtitleBefore}
          <span style={{ color: 'var(--color-primary)', fontWeight: '600' }}>{premiumWord}</span>
          {subtitleAfter}
        </p>

        <div style={{ width: '100%', maxWidth: '100%' }}>
          <label className="form-label">
            {t('lobby.premiumHostSetup.premiumRoomCode')}
          </label>
          <input
            type="text"
            className="input join-room-code-input"
            placeholder={t('common.roomCodePlaceholder')}
            value={roomCode}
            onChange={(e) => handleRoomCodeChange(e.target.value)}
            onPaste={handleRoomCodePaste}
            maxLength={4}
            style={{
              fontSize: '1.8rem',
              letterSpacing: '0.5rem',
              textAlign: 'center',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
            disabled={isCreating}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateHost();
              }
            }}
          />
          <p style={{
            marginTop: '1rem',
            color: 'var(--text-secondary)',
            fontSize: '0.85rem',
            textAlign: 'center',
          }}>
            {t('lobby.premiumHostSetup.hint')}
          </p>
        </div>
      </div>
      <Footer>
        <div className="join-room-buttons">
          <button
            className="button join-room-back-button"
            onClick={onBack}
            disabled={isCreating}
          >
            {t('common.back')}
          </button>
          <button
            className="button join-room-button"
            onClick={handleCreateHost}
            disabled={isCreating || roomCode.trim().length !== 4}
          >
            {isCreating ? t('common.creating') : t('lobby.premiumHostSetup.createRoom')}
          </button>
        </div>
      </Footer>
    </div>
  );
};

export default PremiumHostSetup;
